import type { MateriaalLine, StockPicking, StockMove, ProjectTask } from '@/types/odoo';

export interface FaseGroup {
  taskId: number;
  taskName: string;
  pickingGroups: PickingGroup[];
}

export interface PickingGroup {
  picking: StockPicking;
  lines: MateriaalLine[];
  moves: StockMove[];
}

export const PICKING_STATE_LABELS: Record<string, string> = {
  draft: 'Concept',
  waiting: 'Wachtend',
  confirmed: 'Bevestigd',
  assigned: 'Klaar',
  done: 'Gereed',
  cancel: 'Geannuleerd',
};

export function buildFaseGroups(
  pickings: StockPicking[],
  lines: MateriaalLine[],
  projectTasks: ProjectTask[],
  moves: StockMove[],
): FaseGroup[] {
  const faseMap = new Map<number, FaseGroup>();
  const matchedLineIds = new Set<number>();

  for (const picking of pickings) {
    const taskId = picking.x_studio_projecttaak ? picking.x_studio_projecttaak[0] : 0;
    const taskName = picking.x_studio_projecttaak ? picking.x_studio_projecttaak[1] : 'Onbekende fase';

    let fase = faseMap.get(taskId);
    if (!fase) {
      fase = { taskId, taskName, pickingGroups: [] };
      faseMap.set(taskId, fase);
    }

    const pickingLines = lines.filter((line) => {
      if (line.x_levering && line.x_levering[0] === picking.id) {
        matchedLineIds.add(line.id);
        return true;
      }
      return false;
    });

    const pickingMoves = moves.filter(
      (m) => m.picking_id && m.picking_id[0] === picking.id,
    );

    fase.pickingGroups.push({ picking, lines: pickingLines, moves: pickingMoves });
  }

  // Sort pickings within each fase by name
  for (const fase of faseMap.values()) {
    fase.pickingGroups.sort((a, b) => a.picking.name.localeCompare(b.picking.name));
  }

  // Orphan lines without a matching picking
  const orphanLines = lines.filter((line) => !matchedLineIds.has(line.id));
  if (orphanLines.length > 0) {
    const orphanPicking: StockPicking = {
      id: 0,
      name: 'Overig',
      state: 'draft',
      x_studio_projecttaak: false,
      carrier_tracking_ref: false,
      origin: false,
      partner_id: false,
      create_date: false,
      picking_type_code: 'outgoing',
    };
    let overigFase = faseMap.get(0);
    if (!overigFase) {
      overigFase = { taskId: 0, taskName: 'Overig', pickingGroups: [] };
      faseMap.set(0, overigFase);
    }
    overigFase.pickingGroups.push({ picking: orphanPicking, lines: orphanLines, moves: [] });
  }

  // Add empty fases for parent tasks that have no pickings/lines yet
  // Fases are tasks that other tasks reference as parent_id
  const faseIds = new Set<number>();
  for (const t of projectTasks) {
    if (t.parent_id) faseIds.add(t.parent_id[0]);
  }
  const parentTasks = projectTasks.filter(t => faseIds.has(t.id));
  for (const task of parentTasks) {
    if (!faseMap.has(task.id)) {
      faseMap.set(task.id, {
        taskId: task.id,
        taskName: task.name,
        pickingGroups: [],
      });
    }
  }

  // Flat projects (e.g. EasyKit): no parent/child hierarchy
  // Treat all top-level tasks (no parent_id) as potential upload targets
  if (faseIds.size === 0) {
    for (const task of projectTasks) {
      if (!task.parent_id && !faseMap.has(task.id)) {
        faseMap.set(task.id, {
          taskId: task.id,
          taskName: task.name,
          pickingGroups: [],
        });
      }
    }
  }

  return Array.from(faseMap.values()).sort((a, b) =>
    a.taskName.localeCompare(b.taskName),
  );
}

/**
 * Moves on `picking` that no authored oplijst line accounts for and that are not
 * kit components — i.e. items added directly to the picking in Odoo *after* the
 * oplijst/CSV was uploaded. The authored `x_project_task_line_7edd2` lines stay
 * the display backbone (they carry curated Dutch labels + sequence, and kit
 * lines legitimately have no single move); these unmatched moves are appended
 * as "toegevoegd in Odoo" rows so the portal and the warehouse pick list report
 * everything that is actually on the delivery.
 *
 * Join key is the PRODUCT id (`move.product_id[0]` ↔ `line.x_product[0]`), never
 * the name — `x_name` is curated Dutch while move names are source-language
 * (ADR-051 class of bug).
 */
export function unmatchedMoves(
  picking: StockPicking,
  lines: MateriaalLine[],
  moves: StockMove[],
): StockMove[] {
  const lineProductIds = new Set(
    lines
      .filter((l) => l.x_levering && l.x_levering[0] === picking.id && l.x_product)
      .map((l) => (l.x_product as [number, string])[0]),
  );
  return moves
    .filter(
      (m) =>
        m.picking_id &&
        m.picking_id[0] === picking.id &&
        !m.bom_line_id && // kit components: covered by their kit line
        m.state !== 'cancel' && // don't resurrect cancelled/removed moves
        !(m.product_id && lineProductIds.has(m.product_id[0])),
    )
    .sort((a, b) => a.id - b.id);
}

export function productName(line: MateriaalLine): string {
  // Prefer x_product (Many2one, translated) over x_name (stored text, may be English)
  if (line.x_product) return line.x_product[1];
  if (line.x_name) return line.x_name;
  return 'Onbekend product';
}

export function getAvailabilityBadge(
  line: MateriaalLine,
  picking: StockPicking,
  moves: StockMove[],
): { label: string; className: string } {
  if (!line.x_product) return { label: '-', className: 'bg-gray-100 text-gray-500' };

  // A delivered (done) picking is fully shipped regardless of any move match —
  // its forecast data is gone, so don't fall through to a reservation check.
  if (picking.state === 'done') {
    return { label: 'Geleverd', className: 'bg-green-100 text-green-700' };
  }
  // Only an 'assigned' picking has reservation data. draft / confirmed / waiting
  // are simply not reserved yet — show their neutral state label rather than a
  // false red "niet beschikbaar" (the bug being fixed: nothing reserves until
  // 'assigned', so every in-flight levering would otherwise read as a problem).
  if (picking.state !== 'assigned') {
    return { label: PICKING_STATE_LABELS[picking.state] ?? picking.state, className: 'bg-gray-100 text-gray-600' };
  }

  const productId = line.x_product[0];
  const move = moves.find(
    (m) =>
      m.picking_id &&
      m.picking_id[0] === picking.id &&
      m.product_id &&
      m.product_id[0] === productId,
  );

  if (!move || move.product_uom_qty <= 0) return { label: '-', className: 'bg-gray-100 text-gray-500' };

  // `quantity` is the reserved qty for an assigned picking — the real
  // availability signal. forecast_availability is a warehouse-wide forward
  // forecast and produced false "niet beschikbaar" on delivered lists.
  if (move.quantity >= move.product_uom_qty) {
    return { label: 'Beschikbaar', className: 'bg-green-100 text-green-700' };
  }
  return { label: 'Niet beschikbaar', className: 'bg-red-100 text-red-700' };
}

/** Get parent tasks (fases) from projectTasks — tasks referenced as parent_id by other tasks */
export function getParentTasks(projectTasks: ProjectTask[]): ProjectTask[] {
  const faseIds = new Set<number>();
  for (const t of projectTasks) {
    if (t.parent_id) faseIds.add(t.parent_id[0]);
  }
  return projectTasks.filter((t) => faseIds.has(t.id));
}

/**
 * Map of child taskId → parent (fase) taskId, derived from `t.parent_id`.
 * For Fase 6b: lets callers normalize a child task back to its fase.
 */
export function buildParentByChild(projectTasks: ProjectTask[]): Map<number, number> {
  const parentByChild = new Map<number, number>();
  for (const t of projectTasks) {
    if (t.parent_id) parentByChild.set(t.id, t.parent_id[0]);
  }
  return parentByChild;
}

/**
 * Normalize a task id to its fase id: returns the parent fase id if the task is
 * a child, otherwise the task id itself (it is already a fase / top-level).
 */
export function normalizeFaseId(taskId: number, parentByChild: Map<number, number>): number {
  return parentByChild.get(taskId) ?? taskId;
}

export interface MateriaalTotals {
  /** Total materiaallijsten (pickings) across all fases. */
  totalPickings: number;
  /** Total material lines across all fases. */
  totalLines: number;
  /** Per-fase counts, in the order the fase groups were passed in. */
  perFase: Array<{ taskId: number; taskName: string; pickings: number; lines: number }>;
}

/**
 * Roll up picking + line counts across fase groups (Fase 6c "Alles" view).
 *
 * Counts only — deliberately does NOT compare against the offerte m². Material
 * lines are arbitrary products in arbitrary units (m, stuks, rol, …), not m², so
 * a "% of offerte" here would be meaningless. m² progress lives in the collector
 * status (FaseAggregate / DashboardSysteemBreakdown), not the materiaal roll-up.
 */
export function materiaalTotals(faseGroups: FaseGroup[]): MateriaalTotals {
  let totalPickings = 0;
  let totalLines = 0;
  const perFase = faseGroups.map((fase) => {
    const pickings = fase.pickingGroups.length;
    // Count exactly what MateriaalPickingTable renders as `rowCount`, so the
    // roll-up total equals the sum of the visible rows:
    //   - retour (IN): every move (retours carry no authored lines);
    //   - levering (OUT): authored lines PLUS moves added directly in Odoo.
    const lines = fase.pickingGroups.reduce((sum, pg) => {
      const isRetour = pg.picking.picking_type_code === 'incoming';
      return (
        sum +
        (isRetour ? pg.moves.length : pg.lines.length + unmatchedMoves(pg.picking, pg.lines, pg.moves).length)
      );
    }, 0);
    totalPickings += pickings;
    totalLines += lines;
    return { taskId: fase.taskId, taskName: fase.taskName, pickings, lines };
  });
  return { totalPickings, totalLines, perFase };
}
